import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Eye, EyeOff, Sparkles, Zap, AlertTriangle, CheckCircle, 
  Loader2, X, ChevronDown, ChevronUp, Wand2, BookOpen,
  RefreshCw, Shield, ArrowRight
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Slider } from "../ui/slider";
import { toast } from "sonner";
import { API } from "../../lib/api";

const AIDetectionModal = ({ 
  open, 
  onClose, 
  projectId, 
  chapters = [], 
  currentChapter = 1,
  onContentUpdate 
}) => {
  const [mode, setMode] = useState("detect"); // detect, humanize, batch
  const [selectedChapter, setSelectedChapter] = useState(currentChapter);
  const [intensity, setIntensity] = useState("standard"); // light, standard, aggressive
  
  // Detection state
  const [detecting, setDetecting] = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);
  
  // Humanize state
  const [humanizeJob, setHumanizeJob] = useState(null);
  const [polling, setPolling] = useState(false);
  
  // Batch state
  const [batchJob, setBatchJob] = useState(null);
  
  const [expandedIssues, setExpandedIssues] = useState({});

  useEffect(() => {
    if (open) {
      setSelectedChapter(currentChapter);
      setDetectionResult(null);
      setHumanizeJob(null);
      setBatchJob(null);
    }
  }, [open, currentChapter]);

  const getChapterContent = (chapterNum) => {
    const chapter = chapters.find(c => c.chapter_number === chapterNum);
    return chapter?.content || "";
  };

  // Detection
  const handleDetect = async () => {
    const content = getChapterContent(selectedChapter);
    if (!content || content.length < 100) {
      toast.error("Chapter content too short to analyze");
      return;
    }

    setDetecting(true);
    setDetectionResult(null);

    try {
      const res = await axios.post(`${API}/ai-detection/detect`, {
        project_id: projectId,
        chapter_number: selectedChapter,
        content
      });
      setDetectionResult(res.data);
    } catch (error) {
      toast.error("Detection failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setDetecting(false);
    }
  };

  // Humanize single chapter
  const handleHumanize = async () => {
    const content = getChapterContent(selectedChapter);
    if (!content || content.length < 100) {
      toast.error("Chapter content too short to humanize");
      return;
    }

    try {
      const res = await axios.post(`${API}/ai-detection/humanize/jobs`, {
        project_id: projectId,
        chapter_number: selectedChapter,
        content,
        intensity
      });
      
      setHumanizeJob({ id: res.data.job_id, status: "pending", progress: 0 });
      setPolling(true);
      pollHumanizeJob(res.data.job_id);
    } catch (error) {
      toast.error("Failed to start humanization: " + (error.response?.data?.detail || error.message));
    }
  };

  const pollHumanizeJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/ai-detection/humanize/jobs/${jobId}`);
      setHumanizeJob(res.data);

      if (res.data.status === "completed") {
        setPolling(false);
        toast.success("Humanization complete!");
      } else if (res.data.status === "failed") {
        setPolling(false);
        toast.error("Humanization failed: " + res.data.error);
      } else {
        setTimeout(() => pollHumanizeJob(jobId), 2000);
      }
    } catch (error) {
      setPolling(false);
      toast.error("Failed to check job status");
    }
  };

  const applyHumanizedContent = async () => {
    if (!humanizeJob?.humanized_content) return;

    try {
      // Call parent to update content
      onContentUpdate(selectedChapter, humanizeJob.humanized_content);
      toast.success(`Chapter ${selectedChapter} humanized and saved!`);
      setHumanizeJob(null);
      setDetectionResult(null);
    } catch (error) {
      toast.error("Failed to apply humanized content");
    }
  };

  // Batch humanize
  const handleBatchHumanize = async () => {
    try {
      const res = await axios.post(`${API}/ai-detection/humanize/batch/jobs`, {
        project_id: projectId,
        intensity
      });

      setBatchJob({ id: res.data.job_id, status: "pending", progress: 0, total_chapters: res.data.total_chapters });
      pollBatchJob(res.data.job_id);
    } catch (error) {
      toast.error("Failed to start batch humanization: " + (error.response?.data?.detail || error.message));
    }
  };

  const pollBatchJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/ai-detection/humanize/batch/jobs/${jobId}`);
      setBatchJob(res.data);

      if (res.data.status === "completed") {
        toast.success(`Batch complete! ${res.data.results?.length || 0} chapters processed.`);
      } else if (res.data.status === "failed") {
        toast.error("Batch failed: " + res.data.error);
      } else {
        setTimeout(() => pollBatchJob(jobId), 3000);
      }
    } catch (error) {
      toast.error("Failed to check batch status");
    }
  };

  const applyBatchResults = () => {
    if (!batchJob?.results) return;

    let applied = 0;
    for (const result of batchJob.results) {
      if (result.status === "humanized" && result.humanized_content) {
        onContentUpdate(result.chapter_number, result.humanized_content);
        applied++;
      }
    }

    toast.success(`Applied humanized content to ${applied} chapters!`);
    setBatchJob(null);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "high": return "text-red-400 bg-red-500/20 border-red-500/30";
      case "medium": return "text-amber-400 bg-amber-500/20 border-amber-500/30";
      case "low": return "text-blue-400 bg-blue-500/20 border-blue-500/30";
      default: return "text-slate-400 bg-slate-500/20 border-slate-500/30";
    }
  };

  const getVerdictColor = (verdict) => {
    if (verdict === "Likely AI") return "text-red-400";
    if (verdict === "Likely Human") return "text-emerald-400";
    return "text-amber-400";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="w-5 h-5 text-cyan-400" />
            AI Detection & Humanizer
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Detect AI-generated patterns and make your writing undetectable
          </DialogDescription>
        </DialogHeader>

        {/* Mode Tabs */}
        <div className="flex gap-2 border-b border-slate-700 pb-3">
          {[
            { id: "detect", label: "Detect", icon: Eye },
            { id: "humanize", label: "Humanize Chapter", icon: Wand2 },
            { id: "batch", label: "Clean Entire Book", icon: BookOpen }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={mode === tab.id ? "default" : "ghost"}
              size="sm"
              onClick={() => setMode(tab.id)}
              className={mode === tab.id ? "bg-cyan-600 text-white" : "text-slate-400"}
            >
              <tab.icon className="w-4 h-4 mr-1" />
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {/* DETECT MODE */}
          {mode === "detect" && (
            <div className="space-y-4">
              {/* Chapter selector */}
              <div className="flex items-center gap-4">
                <label className="text-sm text-slate-400">Chapter:</label>
                <select
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(parseInt(e.target.value))}
                  className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-white text-sm"
                >
                  {chapters.map(c => (
                    <option key={c.chapter_number} value={c.chapter_number}>
                      Chapter {c.chapter_number}: {c.title || "Untitled"}
                    </option>
                  ))}
                </select>
                <Button
                  onClick={handleDetect}
                  disabled={detecting}
                  className="bg-cyan-600 hover:bg-cyan-500"
                >
                  {detecting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Eye className="w-4 h-4 mr-2" /> Scan for AI Patterns</>
                  )}
                </Button>
              </div>

              {/* Detection Results */}
              {detectionResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Score Card */}
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm text-slate-400">AI Probability</p>
                        <p className={`text-3xl font-bold ${getVerdictColor(detectionResult.verdict)}`}>
                          {detectionResult.ai_probability}%
                        </p>
                      </div>
                      <Badge className={`text-lg px-4 py-1 ${
                        detectionResult.verdict === "Likely AI" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                        detectionResult.verdict === "Likely Human" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" :
                        "bg-amber-500/20 text-amber-400 border-amber-500/30"
                      }`}>
                        {detectionResult.verdict}
                      </Badge>
                    </div>
                    <Progress 
                      value={detectionResult.ai_probability} 
                      className="h-2 bg-slate-700"
                    />
                    <div className="flex gap-4 mt-3 text-xs text-slate-400">
                      <span>🔴 High: {detectionResult.issue_count.high}</span>
                      <span>🟡 Medium: {detectionResult.issue_count.medium}</span>
                      <span>🔵 Low: {detectionResult.issue_count.low}</span>
                      <span>📝 Words: {detectionResult.word_count}</span>
                    </div>
                  </div>

                  {/* Issues List */}
                  {detectionResult.issues.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-300">
                        Detected Patterns ({detectionResult.issues.length})
                      </p>
                      {detectionResult.issues.slice(0, 15).map((issue, idx) => (
                        <div
                          key={idx}
                          className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-3"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <Badge className={`text-[10px] ${getSeverityColor(issue.severity)}`}>
                                {issue.severity.toUpperCase()}
                              </Badge>
                              <span className="text-sm text-white">{issue.type}</span>
                            </div>
                            <button
                              onClick={() => setExpandedIssues(prev => ({
                                ...prev,
                                [idx]: !prev[idx]
                              }))}
                              className="text-slate-400 hover:text-white"
                            >
                              {expandedIssues[idx] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                          <p className="text-xs text-cyan-400 mt-1 font-mono">"{issue.match}"</p>
                          {expandedIssues[idx] && (
                            <p className="text-xs text-slate-400 mt-2 italic">
                              {issue.sentence}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {detectionResult.ai_probability > 30 && (
                    <Button
                      onClick={() => setMode("humanize")}
                      className="w-full bg-violet-600 hover:bg-violet-500"
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      Humanize This Chapter
                    </Button>
                  )}
                </motion.div>
              )}
            </div>
          )}

          {/* HUMANIZE MODE */}
          {mode === "humanize" && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Chapter:</label>
                  <select
                    value={selectedChapter}
                    onChange={(e) => setSelectedChapter(parseInt(e.target.value))}
                    className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-white text-sm"
                  >
                    {chapters.map(c => (
                      <option key={c.chapter_number} value={c.chapter_number}>
                        Chapter {c.chapter_number}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Intensity:</label>
                  <select
                    value={intensity}
                    onChange={(e) => setIntensity(e.target.value)}
                    className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-white text-sm"
                  >
                    <option value="light">Light (High issues only)</option>
                    <option value="standard">Standard (High + Medium)</option>
                    <option value="aggressive">Aggressive (All issues)</option>
                  </select>
                </div>
              </div>

              {!humanizeJob && (
                <Button
                  onClick={handleHumanize}
                  className="w-full bg-violet-600 hover:bg-violet-500"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Humanize Chapter {selectedChapter}
                </Button>
              )}

              {humanizeJob && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-300">
                      {humanizeJob.status === "completed" ? "Humanization Complete!" : "Processing..."}
                    </span>
                    <Badge className={
                      humanizeJob.status === "completed" ? "bg-emerald-500/20 text-emerald-400" :
                      humanizeJob.status === "failed" ? "bg-red-500/20 text-red-400" :
                      "bg-cyan-500/20 text-cyan-400"
                    }>
                      {humanizeJob.status}
                    </Badge>
                  </div>
                  <Progress value={humanizeJob.progress} className="h-2 bg-slate-700" />

                  {humanizeJob.status === "completed" && humanizeJob.humanized_content && (
                    <div className="mt-4 space-y-3">
                      <div className="bg-slate-900 rounded-lg p-3 max-h-48 overflow-y-auto">
                        <p className="text-xs text-slate-300 whitespace-pre-wrap">
                          {humanizeJob.humanized_content.slice(0, 1000)}
                          {humanizeJob.humanized_content.length > 1000 && "..."}
                        </p>
                      </div>
                      <Button
                        onClick={applyHumanizedContent}
                        className="w-full bg-emerald-600 hover:bg-emerald-500"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Apply Humanized Content
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          )}

          {/* BATCH MODE */}
          {mode === "batch" && (
            <div className="space-y-4">
              <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
                <h4 className="font-medium text-white mb-2">Clean Entire Book</h4>
                <p className="text-sm text-slate-400 mb-4">
                  Process all chapters to remove AI detection markers. This will go through each chapter,
                  detect AI patterns, and rewrite flagged sections to sound naturally human-written.
                </p>

                <div className="mb-4">
                  <label className="text-sm text-slate-400 block mb-1">Intensity:</label>
                  <select
                    value={intensity}
                    onChange={(e) => setIntensity(e.target.value)}
                    className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-white text-sm w-full"
                  >
                    <option value="light">Light — Fix only major AI markers</option>
                    <option value="standard">Standard — Fix high and medium issues</option>
                    <option value="aggressive">Aggressive — Maximum humanization</option>
                  </select>
                </div>

                <p className="text-xs text-slate-500 mb-4">
                  {chapters.filter(c => c.content && c.content.length > 100).length} chapters will be processed
                </p>
              </div>

              {!batchJob && (
                <Button
                  onClick={handleBatchHumanize}
                  className="w-full bg-violet-600 hover:bg-violet-500"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Clean Entire Book ({chapters.length} chapters)
                </Button>
              )}

              {batchJob && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
                >
                  {/* Header with status */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-white">
                      {batchJob.status === "completed" 
                        ? "Batch Complete!" 
                        : batchJob.status === "failed"
                        ? "Batch Failed"
                        : `Processing Chapter ${batchJob.current_chapter || 0} of ${batchJob.total_chapters || chapters.length}`
                      }
                    </span>
                    <Badge className={
                      batchJob.status === "completed" ? "bg-emerald-500/20 text-emerald-400" :
                      batchJob.status === "failed" ? "bg-red-500/20 text-red-400" :
                      "bg-cyan-500/20 text-cyan-400"
                    }>
                      {batchJob.status}
                    </Badge>
                  </div>

                  {/* Big progress bar */}
                  <div className="relative mb-3">
                    <Progress value={batchJob.progress || 0} className="h-4 bg-slate-700 rounded-full" />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white drop-shadow">
                      {batchJob.progress || 0}%
                    </span>
                  </div>

                  {/* Live chapter-by-chapter log */}
                  {batchJob.status !== "completed" && batchJob.current_chapter > 0 && (
                    <div className="flex items-center gap-2 text-sm text-cyan-400 mb-3">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Working on Chapter {batchJob.current_chapter}...</span>
                    </div>
                  )}

                  {/* Results log (scrollable) */}
                  {batchJob.results && batchJob.results.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-1 mb-3 border border-slate-700/50 rounded-lg p-2 bg-slate-900/50">
                      {batchJob.results.map((r, idx) => (
                        <div key={idx} className={`text-xs px-2 py-1 rounded flex items-center justify-between ${
                          r.status === "humanized" ? "bg-emerald-500/10 text-emerald-400" :
                          r.status === "clean" ? "bg-slate-500/10 text-slate-400" :
                          r.status === "skipped" ? "bg-slate-500/10 text-slate-500" :
                          "bg-red-500/10 text-red-400"
                        }`}>
                          <span>
                            Ch {r.chapter_number}: {
                              r.status === "humanized" 
                                ? `Rewritten (${r.paragraphs_rewritten || '?'}/${r.paragraphs_total || '?'} paragraphs)` 
                                : r.status === "clean" 
                                ? r.reason || "Clean — no changes needed"
                                : r.status === "skipped"
                                ? r.reason || "Skipped"
                                : `Failed: ${r.error || 'Unknown error'}`
                            }
                          </span>
                          <span>{
                            r.status === "humanized" ? "✅" :
                            r.status === "clean" ? "⏭️" :
                            r.status === "skipped" ? "⏭️" : "❌"
                          }</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Summary when complete */}
                  {batchJob.status === "completed" && batchJob.results && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-emerald-500/10 rounded-lg p-2">
                          <div className="text-lg font-bold text-emerald-400">
                            {batchJob.results.filter(r => r.status === "humanized").length}
                          </div>
                          <div className="text-xs text-slate-400">Rewritten</div>
                        </div>
                        <div className="bg-slate-500/10 rounded-lg p-2">
                          <div className="text-lg font-bold text-slate-400">
                            {batchJob.results.filter(r => r.status === "clean" || r.status === "skipped").length}
                          </div>
                          <div className="text-xs text-slate-400">Already Clean</div>
                        </div>
                        <div className="bg-red-500/10 rounded-lg p-2">
                          <div className="text-lg font-bold text-red-400">
                            {batchJob.results.filter(r => r.status === "failed").length}
                          </div>
                          <div className="text-xs text-slate-400">Failed</div>
                        </div>
                      </div>
                      <Button
                        onClick={applyBatchResults}
                        className="w-full bg-emerald-600 hover:bg-emerald-500"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Apply All Humanized Chapters
                      </Button>
                    </div>
                  )}

                  {batchJob.status === "failed" && (
                    <p className="text-sm text-red-400 mt-2">{batchJob.error}</p>
                  )}
                </motion.div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIDetectionModal;
